import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/cn"
import { fmtRelativeTime } from "@/lib/format"
import type { VersionDotRow } from "@/types/version_dot"

type Props = {
  versions: VersionDotRow[]
  // Row id of the selected version (matches `VersionDotRow.id`).
  // Used by both Skills (SkillVersion id) and Escalation Policies
  // (EscalationPolicySet id) — same dot, different parent table.
  selectedVersionId: number
  // Emits the clicked row id; parent maps id → URL nav.
  onSelect: (versionId: number) => void
}

// Version chips are wide because they carry name/status/byline. Measure the
// available strip and render only full chips so the newest version can stay
// anchored at the right edge instead of disappearing behind overflow.
const MIN_VISIBLE = 4
const DEFAULT_VISIBLE = 5
const MAX_VISIBLE = 5
const VERSION_SLOT_WIDTH = 176
const ARROW_RESERVE_WIDTH = 88

// Horizontal timeline strip: one dot per version in the lineage,
// colored by status. Click a dot to switch the page's selected version.
//
// Lineages can grow long (one tenant has 11+ sets and counting), so the
// strip is windowed to the number of full chips that fit. The newest version
// stays pinned as the rightmost chip while the older side pages with chevrons.
// The window auto-scrolls to keep the selected older version in view whenever
// the operator clicks an off-screen dot or reloads the page on a different
// version.
export function VersionTimeline({ versions, selectedVersionId, onSelect }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [targetVisible, setTargetVisible] = useState(DEFAULT_VISIBLE)

  // Show oldest → newest left-to-right (reverse of the table's newest-first).
  const ordered = useMemo(
    () => [...versions].sort((a, b) => a.version - b.version),
    [versions],
  )
  const total = ordered.length
  const measuredVisible = Math.max(1, Math.min(targetVisible, total || targetVisible))
  const visibleCount = total > 0 && total <= MAX_VISIBLE ? total : measuredVisible
  const pinLatest = total > visibleCount
  const latest = ordered[total - 1] ?? null
  const older = useMemo(
    () => pinLatest ? ordered.slice(0, -1) : ordered,
    [ordered, pinLatest],
  )
  const olderSlotCount = pinLatest ? Math.max(1, visibleCount - 1) : visibleCount
  const maxStart = Math.max(0, older.length - olderSlotCount)

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const updateTargetVisible = () => {
      const width = node.getBoundingClientRect().width
      const usable = Math.max(0, width - ARROW_RESERVE_WIDTH)
      const next = clampVisible(Math.floor(usable / VERSION_SLOT_WIDTH))
      setTargetVisible(next)
    }

    updateTargetVisible()
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateTargetVisible)
      return () => window.removeEventListener("resize", updateTargetVisible)
    }

    const observer = new ResizeObserver(updateTargetVisible)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // Land on whichever page contains the selected version. Default to the
  // newest page when the selected id isn't found in the lineage.
  const [start, setStart] = useState<number>(() => {
    const idx = older.findIndex((v) => v.id === selectedVersionId)
    if (idx < 0) return maxStart
    return clampStart(idx - Math.floor(olderSlotCount / 2), maxStart)
  })

  useEffect(() => {
    setStart((curr) => clampStart(curr, maxStart))
  }, [maxStart])

  // Re-center if the selected version moves outside the current window
  // (e.g. operator clicked a dot far away, or Inertia revisited a
  // different version). `start` is intentionally NOT in the deps —
  // chevron-driven start changes would re-trigger this effect and snap
  // the window back when the selected version was on a different page.
  // Functional setState reads the current `start` without re-running.
  useEffect(() => {
    const idx = older.findIndex((v) => v.id === selectedVersionId)
    if (idx < 0) return
    setStart((curr) => {
      if (idx < curr || idx >= curr + olderSlotCount) {
        return clampStart(idx - Math.floor(olderSlotCount / 2), maxStart)
      }
      return curr
    })
  }, [selectedVersionId, older, olderSlotCount, maxStart])

  const visible    = pinLatest && latest
    ? [...older.slice(start, start + olderSlotCount), latest]
    : older.slice(start, start + olderSlotCount)
  const canPrev    = start > 0
  const canNext    = start < maxStart
  const showArrows = total > visibleCount
  const olderWindowEnd = Math.min(start + olderSlotCount, older.length)
  const windowLabel = pinLatest && latest
    ? `${start + 1}-${olderWindowEnd} + v${latest.version}`
    : `${start + 1}-${Math.min(start + olderSlotCount, total)} of ${total}`

  const stepPrev = () => setStart((s) => clampStart(s - olderSlotCount, maxStart))
  const stepNext = () => setStart((s) => clampStart(s + olderSlotCount, maxStart))

  return (
    // `min-w-0` on the outer flex container is the load-bearing class:
    // without it, the inner content-sized version row makes flex-1 take
    // its natural width and pushes the page horizontally on mobile (the
    // EscalationPolicies/Index 1237 px overflow bug). With min-w-0, the
    // version row can shrink and `overflow-x-auto` kicks in for the
    // visible window.
    <div ref={containerRef} className="flex min-w-0 items-stretch gap-1 py-1">
      {showArrows && (
        <ArrowButton
          onClick={stepPrev}
          disabled={!canPrev}
          ariaLabel="Older versions"
        >
          <ChevronLeft size={14} />
        </ArrowButton>
      )}

      <div className="flex min-w-0 flex-1 items-center justify-end gap-1 overflow-x-auto thin-scrollbar">
        {visible.map((v, idx) => {
          const here = v.id === selectedVersionId
          const next = visible[idx + 1]
          const isLastInWindow = idx === visible.length - 1
          const hasGapToNext = next != null && next.version - v.version > 1
          return (
            <div key={v.id} className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => onSelect(v.id)}
                className={cn(
                  "group flex min-h-[96px] min-w-[132px] shrink-0 flex-col items-center justify-center gap-1 rounded-control px-4 py-3 text-xs transition sm:min-w-[148px]",
                  here ? "bg-white/5" : "hover:bg-white/[0.03]",
                )}
              >
                <Dot status={v.status} highlighted={here} />
                <span className={cn("max-w-[160px] truncate font-medium", here ? "text-app-text" : "text-app-muted")}>
                  {v.name ? `(v${v.version}) - ${v.name}` : `v${v.version}`}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-app-subtle">
                  {v.status}
                </span>
                <span className="text-[10px] text-app-subtle">
                  {(v.lastEditedByUserName || v.createdByUserName) && (
                    <>{v.lastEditedByUserName ?? v.createdByUserName} · </>
                  )}
                  {fmtRelativeTime(v.promotedAt ?? v.updatedAt)}
                </span>
              </button>
              {!isLastInWindow && (
                hasGapToNext ? (
                  <span className="w-6 shrink-0 text-center text-xs text-app-subtle" aria-hidden>...</span>
                ) : (
                  <span className="h-px w-6 shrink-0 bg-border" aria-hidden />
                )
              )}
            </div>
          )
        })}
      </div>

      {showArrows && (
        <div className="flex flex-col items-end justify-center gap-1">
          <ArrowButton
            onClick={stepNext}
            disabled={!canNext}
            ariaLabel="Newer versions"
          >
            <ChevronRight size={14} />
          </ArrowButton>
          <span className="px-1 text-[10px] text-app-subtle tabular-nums">
            {windowLabel}
          </span>
        </div>
      )}
    </div>
  )
}

function clampStart(next: number, maxStart: number): number {
  if (next < 0) return 0
  if (next > maxStart) return maxStart
  return next
}

function clampVisible(next: number): number {
  if (next < MIN_VISIBLE) return MIN_VISIBLE
  if (next > MAX_VISIBLE) return MAX_VISIBLE
  return next
}

function ArrowButton({
  onClick, disabled, ariaLabel, children,
}: {
  onClick: () => void
  disabled: boolean
  ariaLabel: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-control border border-border transition",
        disabled
          ? "cursor-not-allowed opacity-30"
          : "text-app-muted hover:bg-white/5 hover:text-app-text",
      )}
    >
      {children}
    </button>
  )
}

function Dot({ status, highlighted }: { status: VersionDotRow["status"]; highlighted: boolean }) {
  const color =
    status === "live"     ? "bg-vip-gold border-vip-gold"
  : status === "draft"    ? "bg-brand-red border-brand-red"
  :                          "border-border-strong bg-transparent"
  return (
    <span
      className={cn(
        "h-3 w-3 rounded-full border transition",
        color,
        highlighted ? "ring-2 ring-offset-2 ring-offset-app-bg" : "",
        highlighted && status === "live"   ? "ring-vip-gold/50" : "",
        highlighted && status === "draft"  ? "ring-brand-red/50" : "",
        highlighted && status === "archived" ? "ring-border-strong" : "",
      )}
      aria-hidden
    />
  )
}
